import type { BeforeErrorHook } from "ky";
import { isHTTPError } from "ky";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import store from "@/redux/store";

import type { ApiContext } from "../types";

import type { ServerError } from "./errorShape";

// addToast is untyped JS — cast to make fields optional as they are in practice
type AddToastParams = {
  dismissTimeout?: number;
  hideReportButton?: boolean;
  message?: string;
  messageType?: string;
  requestId?: string | null;
};
const typedAddToast = addToast as unknown as (params: AddToastParams) => unknown;

// store.dispatch is typed as Dispatch<AnyAction> but toaster actions are plain objects
const dispatch: (action: any) => any = store.dispatch;

// Intentionally excluded from base.ts and public.ts — the unauthenticated http instance
// rejects with cleanError only (no toast), matching legacy axios.js behavior for `http`.
// All authenticated instances (auth, background, workflow, calendar) include this hook.
export const errorToastHook: BeforeErrorHook = ({ error, options }) => {
  if (!isHTTPError(error)) return error;

  const status = error.response.status;
  const isNonAuthFailure = status !== 401 && status !== 403;

  if (!isNonAuthFailure) return error;

  // cleanError is set by errorShapeHook (runs first on base)
  const cleanError = (error as typeof error & { cleanError?: ServerError }).cleanError ?? {};
  const {
    message,
    request_id: requestId,
    type,
  } = cleanError as ServerError & {
    request_id?: string;
  };

  const context = (options.context as ApiContext | undefined) ?? {};
  const {
    conditionalExceptionReporting,
    exceptionConfig,
    preventAllErrorToast,
    toastErrorType: defaultToastErrorType,
  } = context;

  const config = exceptionConfig?.[type ?? ""];
  const preventToast = preventAllErrorToast || config?.preventToast;
  const toastDismissTimeout = config?.toastDismissTimeout ?? 10000;
  const toastErrorType = config?.toastErrorType ?? defaultToastErrorType;

  if (!preventToast) {
    if (toastErrorType) {
      dispatch(
        typedAddToast({
          dismissTimeout: toastDismissTimeout,
          message,
          messageType: toastErrorType,
        }),
      );
    } else {
      // Conditionally include requestId — for 5xx show it, for 4xx hide it when
      // conditionalExceptionReporting is set
      const optionalRequestId = conditionalExceptionReporting
        ? status >= 500
          ? requestId
          : null
        : requestId;

      dispatch(
        typedAddToast({
          dismissTimeout: toastDismissTimeout,
          hideReportButton: optionalRequestId === undefined,
          message,
          messageType: toastMessageTypes.error_v2,
          requestId: optionalRequestId,
        }),
      );
    }
  }

  config?.errorCallback?.(cleanError);

  return error;
};
